import React, { useEffect, useMemo, useState } from 'react';
import TextField from 'components/Form/TextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import Spacer from 'components/Spacer';
import { Icon, Icons } from 'components/Icon';
import { BeneficiaryTypeEnum } from '../types/user.enum.type';
import CustomSelect from 'components/Form/CustomSelect';
import { RelationshipType } from 'constants/bank-account-type';
import { MaritalStatus } from 'constants/marital-status';
import {
  useCreateBeneficiary,
  useDeleteBeneficiary,
  useGetBeneficiaryList
} from 'common/queries-and-mutations/beneficiary';
import { Loader } from 'components/Loader';
import { findNextPage, findPreviousPage, showRemoveButton } from 'utils';
import { useGetChildrenList } from 'common/queries-and-mutations/children';
import { useGetSpousesList } from 'common/queries-and-mutations/spouse';
import SelectMultipleBeneficiaryModal, {
  SpouseChildDto
} from 'components/Modal/SelectMultipleBeneficiaryModal';
import GenderField from 'components/Form/GenderField';
import InfoBar from './InfoBar';
import { STEPS } from 'constants/steps';
import { fetchUserInformation } from 'thunks/account-thunk';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';

export default function BeneficiaryInfo() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSelectUser, setShowSelectUser] = useState(false);
  const [isLater, setIsLater] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAccountDetails);
  const planAllowPage = user && JSON.parse(user?.planAllowPage);

  const { mutate, isSuccess, isLoading, isError } = useCreateBeneficiary();
  const { mutate: deleteBeneficiary, isLoading: isDeleting } =
    useDeleteBeneficiary();

  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const {
    data,
    isSuccess: isBeneficiarySuccess,
    isLoading: isBeneficiaryLoading
  } = useGetBeneficiaryList();

  const { data: childrens } = useGetChildrenList();

  const { data: spouses } = useGetSpousesList();

  const beneficiaries = useMemo(() => {
    if (isBeneficiarySuccess && data.length > 0) {
      return data.map(beneficiary => {
        return {
          beneficiaryId: beneficiary.id,
          beneficiaryFirstName: beneficiary.beneficiaryFirstName,
          beneficiaryMiddleName: beneficiary.beneficiaryMiddleName,
          beneficiaryLastName: beneficiary.beneficiaryLastName,
          beneficiaryType: beneficiary.beneficiaryType,
          relationship: beneficiary.relationship,
          address: beneficiary.address,
          emailAddress: beneficiary.emailAddress,
          dateOfBirth: beneficiary.dateOfBirth,
          maritalStatus: beneficiary.maritalStatus,
          rcNumber: beneficiary.rcNumber,
          incorporationDate: beneficiary.incorporationDate,
          incorporationPlace: beneficiary.incorporationPlace,
          gender: beneficiary.gender
        };
      });
    } else {
      return [
        {
          beneficiaryId: null,
          beneficiaryFirstName: '',
          beneficiaryMiddleName: '',
          beneficiaryLastName: '',
          beneficiaryType: '',
          relationship: '',
          address: '',
          emailAddress: '',
          dateOfBirth: '',
          maritalStatus: '',
          rcNumber: '',
          incorporationDate: '',
          incorporationPlace: '',
          gender: ''
        }
      ];
    }
  }, [data, isBeneficiarySuccess]);

  const formik = useFormik({
    initialValues: {
      beneficiaries
    },
    validationSchema: Yup.object({
      beneficiaries: Yup.array().of(
        Yup.object().shape({
          beneficiaryFirstName: Yup.string().when('beneficiaryType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string()
              .required('Beneficiary first name is required')
              .nullable(),
            otherwise: Yup.string().required('Name is required').nullable()
          }),
          beneficiaryLastName: Yup.string().when('beneficiaryType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string()
              .required('Beneficiary last name is required')
              .nullable(),
            otherwise: Yup.string()
          }),
          beneficiaryType: Yup.string()
            .required('Beneficiary type is required')
            .nullable(),
          rcNumber: Yup.string().when('beneficiaryType', {
            is: BeneficiaryTypeEnum.CORPORATE,
            then: Yup.string().required('RC Number is required').nullable(),
            otherwise: Yup.string()
          }),
          dateOfBirth: Yup.string().when('beneficiaryType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string().required('Date of Birth is required').nullable(),
            otherwise: Yup.string()
          }),
          relationship: Yup.string().when('beneficiaryType', {
            is: BeneficiaryTypeEnum.INDIVIDUAL,
            then: Yup.string().required('Relationship is required').nullable(),
            otherwise: Yup.string()
          }),
          address: Yup.string().required('Address is required').nullable(),
          gender: Yup.string().required('Gender is required').nullable(),
          maritalStatus: Yup.string()
            .required('Marital status is required')
            .nullable(),
          emailAddress: Yup.string()
            .email('Invalid email address')
            .required('Email is required')
            .nullable()
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onBeneficarySubmit(values);
    }
  });

  const addMoreBeneficary = () => {
    formik.values.beneficiaries.push({
      beneficiaryId: null,
      beneficiaryFirstName: '',
      beneficiaryMiddleName: '',
      beneficiaryLastName: '',
      beneficiaryType: '',
      relationship: '',
      address: '',
      emailAddress: '',
      dateOfBirth: '',
      maritalStatus: '',
      rcNumber: '',
      incorporationDate: '',
      incorporationPlace: '',
      gender: ''
    });
    formik.setFieldValue('beneficiaries', [...formik.values.beneficiaries]);
  };

  const removeBeneficary = (
    indexToRemove: number,
    beneficiaryId?: number | null
  ) => {
    if (beneficiaryId) {
      deleteBeneficiary(beneficiaryId);
    }
    formik.values.beneficiaries.splice(indexToRemove, 1);
    formik.setFieldValue('beneficiaries', [...formik.values.beneficiaries]);
  };

  const onBeneficarySubmit = (formValue: any) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      dispatch(fetchUserInformation());
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.BENEFICIARY_INFORMATION));
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, isLater, dispatch, navigate, planAllowPage]);

  const onContinueLater = () => {
    if (Object.keys(formik.errors).length) {
      return;
    }
    mutate(formik.values);
  };

  const shouldShowSelectField =
    (spouses && spouses?.length > 0) || (childrens && childrens?.length > 0);

  const onBeneficiarySelect = (spouseChildDto: SpouseChildDto[]) => {
    const children = spouseChildDto.filter(
      item => item.relationship === 'Child'
    );
    let childBeneficiary: any[] = [];
    let spouseBeneficiary: any[] = [];
    if (children.length) {
      childBeneficiary = children.map(child => ({
        childrenId: parseInt((child.id.match(/\d+/) ?? ['0'])[0]),
        beneficiaryFirstName: child.firstName,
        beneficiaryMiddleName: child.middleName,
        beneficiaryLastName: child.lastName,
        beneficiaryType: BeneficiaryTypeEnum.INDIVIDUAL,
        relationship: child.relationship,
        address: child.address,
        gender: child.gender,
        emailAddress: '',
        dateOfBirth: child.dateOfBirth,
        maritalStatus: '',
        rcNumber: '',
        incorporationDate: '',
        incorporationPlace: ''
      }));
    }
    const spouses = spouseChildDto.filter(
      item => item.relationship === 'Spouse'
    );
    if (spouses.length) {
      spouseBeneficiary = spouses.map(spouse => ({
        spouseId: parseInt((spouse.id.match(/\d+/) ?? ['0'])[0]),
        beneficiaryFirstName: spouse.firstName,
        beneficiaryMiddleName: spouse.middleName,
        beneficiaryLastName: spouse.lastName,
        beneficiaryType: BeneficiaryTypeEnum.INDIVIDUAL,
        relationship: spouse.relationship,
        address: spouse.address,
        emailAddress: '',
        dateOfBirth: spouse.dateOfBirth,
        maritalStatus: '',
        rcNumber: '',
        incorporationDate: '',
        incorporationPlace: ''
      }));
    }
    formik.setFieldValue(`beneficiaries`, [
      ...formik.values.beneficiaries.filter(
        bene => bene.beneficiaryType !== ''
      ),
      ...spouseBeneficiary,
      ...childBeneficiary
    ]);

    setShowSelectUser(false);
  };

  const onPrevious = () => {
    navigate(findPreviousPage(planAllowPage, STEPS.BENEFICIARY_INFORMATION));
  };

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={2}
        stepPercentage={STEPS.BENEFICIARY_INFORMATION}
        timelineStep={2}
      />
      {isBeneficiaryLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
            Beneficiary Information
          </p>

          <form
            autoComplete="false"
            className="m-10"
            onSubmit={formik.handleSubmit}
          >
            {shouldShowSelectField && (
              <div
                className="mb-3 mt-8 flex cursor-pointer items-center gap-1"
                role="button"
                onClick={() => setShowSelectUser(true)}
              >
                <Icon name={Icons.Add} width={15} height={15} fill="#FF6606" />
                <p className="font-circular text-base font-medium text-primary-800">
                  Select from Spouse & Children
                </p>
              </div>
            )}
            {formik.values.beneficiaries.map((beneficiary, index) => (
              <div key={index} className="mb-8">
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`beneficiaries[${index}].beneficiaryFirstName`}
                      placeholder=""
                      value={beneficiary.beneficiaryFirstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label={
                        beneficiary.beneficiaryType ===
                        BeneficiaryTypeEnum.INDIVIDUAL
                          ? 'Beneficiary First Name'
                          : 'Name'
                      }
                      error={
                        formik.touched.beneficiaries
                          ? formik.errors.beneficiaries?.[index] &&
                            formik.errors.beneficiaries.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.beneficiaries[index]
                                )
                              ).beneficiaryFirstName
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <div className="relative mb-2 flex items-center justify-between">
                      <span className="flex items-center">
                        <label className="text-[12px] font-medium text-[#353535]">
                          Beneficiary Type
                        </label>
                        <div className="mx-2 mb-2">
                          <Spacer width={3} />
                          <Icon name={Icons.Required} />
                        </div>
                      </span>
                    </div>
                    <div className="mt-5 flex items-center gap-10">
                      <button
                        type="button"
                        onClick={() =>
                          formik.setFieldValue(
                            `beneficiaries[${index}].beneficiaryType`,
                            BeneficiaryTypeEnum.INDIVIDUAL
                          )
                        }
                        className="flex items-center gap-3"
                      >
                        <span className="text-base font-bold text-[#353535]">
                          Individual
                        </span>
                        <Icon
                          name={
                            beneficiary.beneficiaryType ===
                            BeneficiaryTypeEnum.INDIVIDUAL
                              ? Icons.CheckedBox
                              : Icons.UncheckBox
                          }
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          formik.setFieldValue(
                            `beneficiaries[${index}].beneficiaryType`,
                            BeneficiaryTypeEnum.CORPORATE
                          )
                        }
                        className="flex items-center gap-3"
                      >
                        <span className="text-base font-bold text-[#353535]">
                          Corporate
                        </span>
                        <Icon
                          name={
                            beneficiary.beneficiaryType ===
                            BeneficiaryTypeEnum.CORPORATE
                              ? Icons.CheckedBox
                              : Icons.UncheckBox
                          }
                        />
                      </button>
                    </div>
                  </div>
                </div>
                {beneficiary.beneficiaryType ===
                  BeneficiaryTypeEnum.INDIVIDUAL && (
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`beneficiaries[${index}].beneficiaryMiddleName`}
                        placeholder=""
                        value={beneficiary.beneficiaryMiddleName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Beneficiary Middle Name"
                        error={
                          formik.touched.beneficiaries
                            ? formik.errors.beneficiaries?.[index] &&
                              formik.errors.beneficiaries.length > 0
                              ? JSON.parse(
                                  JSON.stringify(
                                    formik.errors.beneficiaries[index]
                                  )
                                ).beneficiaryMiddleName
                              : ''
                            : ''
                        }
                        isRequired
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`beneficiaries[${index}].beneficiaryLastName`}
                        placeholder=""
                        value={beneficiary.beneficiaryLastName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Beneficiary Last Name"
                        error={
                          formik.touched.beneficiaries
                            ? formik.errors.beneficiaries?.[index] &&
                              formik.errors.beneficiaries.length > 0
                              ? JSON.parse(
                                  JSON.stringify(
                                    formik.errors.beneficiaries[index]
                                  )
                                ).beneficiaryLastName
                              : ''
                            : ''
                        }
                        isRequired
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  </div>
                )}
                {beneficiary.beneficiaryType ===
                  BeneficiaryTypeEnum.INDIVIDUAL && (
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <CustomSelect
                        options={RelationshipType}
                        selectedOption={{
                          value: beneficiary.relationship,
                          label: beneficiary.relationship
                        }}
                        handleOptionChange={option => {
                          formik.setFieldValue(
                            `beneficiaries[${index}].relationship`,
                            option?.value
                          );
                        }}
                        label="Relationship"
                        name="relationship"
                        isRequired
                        isSearchable
                        className="h-[3.5rem] rounded-2xl"
                        error={
                          formik.touched.beneficiaries
                            ? formik.errors.beneficiaries?.[index] &&
                              formik.errors.beneficiaries.length > 0
                              ? JSON.parse(
                                  JSON.stringify(
                                    formik.errors.beneficiaries[index]
                                  )
                                ).relationship
                              : ''
                            : ''
                        }
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`beneficiaries[${index}].address`}
                        placeholder=""
                        isRequired
                        value={beneficiary.address}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Home Address"
                        error={
                          formik.touched.beneficiaries
                            ? formik.errors.beneficiaries?.[index] &&
                              formik.errors.beneficiaries.length > 0
                              ? JSON.parse(
                                  JSON.stringify(
                                    formik.errors.beneficiaries[index]
                                  )
                                ).address
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  </div>
                )}
                {beneficiary.beneficiaryType ===
                  BeneficiaryTypeEnum.CORPORATE && (
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`beneficiaries[${index}].rcNumber`}
                        placeholder=""
                        value={beneficiary.rcNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="RC Number"
                        isRequired
                        error={
                          formik.touched.beneficiaries
                            ? formik.errors.beneficiaries?.[index] &&
                              formik.errors.beneficiaries.length > 0
                              ? JSON.parse(
                                  JSON.stringify(
                                    formik.errors.beneficiaries[index]
                                  )
                                ).rcNumber
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`beneficiaries[${index}].address`}
                        placeholder=""
                        isRequired
                        value={beneficiary.address}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Company Address"
                        error={
                          formik.touched.beneficiaries
                            ? formik.errors.beneficiaries?.[index] &&
                              formik.errors.beneficiaries.length > 0
                              ? JSON.parse(
                                  JSON.stringify(
                                    formik.errors.beneficiaries[index]
                                  )
                                ).address
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  </div>
                )}
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    {beneficiary.beneficiaryType && (
                      <TextField
                        name={`beneficiaries[${index}].emailAddress`}
                        placeholder=""
                        isRequired
                        value={beneficiary.emailAddress}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Email Address"
                        error={
                          formik.touched.beneficiaries
                            ? formik.errors.beneficiaries?.[index] &&
                              formik.errors.beneficiaries.length > 0
                              ? JSON.parse(
                                  JSON.stringify(
                                    formik.errors.beneficiaries[index]
                                  )
                                ).emailAddress
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    )}
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    {beneficiary.beneficiaryType ===
                      BeneficiaryTypeEnum.INDIVIDUAL && (
                      <TextField
                        name={`beneficiaries[${index}].dateOfBirth`}
                        type="date"
                        isRequired
                        placeholder=""
                        value={beneficiary.dateOfBirth}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Date of Birth"
                        error={
                          formik.touched.beneficiaries
                            ? formik.errors.beneficiaries?.[index] &&
                              formik.errors.beneficiaries.length > 0
                              ? JSON.parse(
                                  JSON.stringify(
                                    formik.errors.beneficiaries[index]
                                  )
                                ).dateOfBirth
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                        validateDate={false}
                      />
                    )}
                    {beneficiary.beneficiaryType ===
                      BeneficiaryTypeEnum.CORPORATE && (
                      <TextField
                        name={`beneficiaries[${index}].incorporationDate`}
                        type="date"
                        placeholder=""
                        value={beneficiary.incorporationDate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Date of Incorporation"
                        error={
                          formik.touched.beneficiaries
                            ? formik.errors.beneficiaries?.[index] &&
                              formik.errors.beneficiaries.length > 0
                              ? JSON.parse(
                                  JSON.stringify(
                                    formik.errors.beneficiaries[index]
                                  )
                                ).incorporationDate
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    )}
                  </div>
                </div>
                {beneficiary.beneficiaryType ===
                  BeneficiaryTypeEnum.INDIVIDUAL && (
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <CustomSelect
                        options={MaritalStatus}
                        selectedOption={{
                          value: beneficiary.maritalStatus,
                          label: beneficiary.maritalStatus
                        }}
                        handleOptionChange={option => {
                          formik.setFieldValue(
                            `beneficiaries[${index}].maritalStatus`,
                            option?.value
                          );
                        }}
                        label="Marital Status"
                        name="maritalStatus"
                        isSearchable
                        className="h-[3.5rem] rounded-2xl"
                        error={
                          formik.touched.beneficiaries
                            ? formik.errors.beneficiaries?.[index] &&
                              formik.errors.beneficiaries.length > 0
                              ? JSON.parse(
                                  JSON.stringify(
                                    formik.errors.beneficiaries[index]
                                  )
                                ).maritalStatus
                              : ''
                            : ''
                        }
                        isRequired
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <GenderField
                        gender={beneficiary.gender}
                        onSelect={gender =>
                          formik.setFieldValue(
                            `beneficiaries[${index}].gender`,
                            gender
                          )
                        }
                        isRequired
                      />
                    </div>
                  </div>
                )}
                {beneficiary.beneficiaryType ===
                  BeneficiaryTypeEnum.CORPORATE && (
                  <div className="grid md:grid-cols-2 md:gap-10">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`beneficiaries[${index}].incorporationPlace`}
                        placeholder=""
                        value={beneficiary.incorporationPlace}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Place of Incorporation"
                        error={
                          formik.touched.beneficiaries
                            ? formik.errors.beneficiaries?.[index] &&
                              formik.errors.beneficiaries.length > 0
                              ? JSON.parse(
                                  JSON.stringify(
                                    formik.errors.beneficiaries[index]
                                  )
                                ).incorporationPlace
                              : ''
                            : ''
                        }
                        size="lg"
                        className="rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  </div>
                )}
                {showRemoveButton(
                  index,
                  beneficiary.beneficiaryId,
                  data!.length > 1
                ) && (
                  <div className="group relative z-0 mt-1 w-full">
                    {isDeleting ? (
                      <Loader height={20} width={20} type={'Wait'} />
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          removeBeneficary(index, beneficiary.beneficiaryId)
                        }
                      >
                        <Icon name={Icons.Delete} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center justify-between gap-2">
              <div className="flex w-full items-center gap-2">
                <Icon name={Icons.Required} />
                <span className="text-[10px] text-[#7A7C87]">
                  Indicates Required
                </span>
              </div>
              {formik.values.beneficiaries[0].beneficiaryType && (
                <div className="group z-0 flex w-full items-center justify-end">
                  <div
                    className="flex items-center gap-4"
                    onClick={addMoreBeneficary}
                    role="button"
                    tabIndex={-1}
                  >
                    <Icon name={Icons.AddCircleOutline} />
                    <span className="mt-2 text-base font-medium text-[#353535]">
                      Add Another Beneficiary
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 flex w-full items-end justify-between">
              <Button
                variant="outline"
                size="md"
                className="mt-1 w-[9rem] rounded p-[0.1rem] text-primary-900"
                label="Previous"
                type="button"
                onClick={() => onPrevious()}
              ></Button>
              <div className="mt-5 flex items-end justify-end gap-8">
                {parentPath !== 'dashboard' && (
                  <button
                    type="submit"
                    className="font-circular text-[15px] font-bold text-[#353535] underline"
                    onClick={() => {
                      onContinueLater();
                      setIsLater(true);
                    }}
                  >
                    Save and Continue Later
                  </button>
                )}
                <Button
                  variant="primary"
                  size="md"
                  className="mt-5 w-[9rem] rounded p-[0.1rem]"
                  label="Proceed"
                  type="submit"
                  disabled={!formik.isValid}
                  loading={isSubmitting || isLoading}
                ></Button>
              </div>
            </div>
          </form>
        </>
      )}
      {showSelectUser && (
        <SelectMultipleBeneficiaryModal
          onClose={() => setShowSelectUser(false)}
          onAdd={selectedUser => onBeneficiarySelect(selectedUser)}
        />
      )}
    </div>
  );
}
